import { FC } from 'react'

import { useParams, type Params } from 'react-router'

import { legacyMatchPath } from '@bettermode/common/legacy-match-path'
import { isForbidden, isNotFound } from '@tribeplatform/gql-client/lib'
import { Network, type Space as TSpace } from '@tribeplatform/gql-client/types'
import { useI18n } from '@tribeplatform/react-components/i18n'
import { SlateLayout } from '@tribeplatform/react-components/LayoutFrame'
import { useRouterPath } from '@tribeplatform/react-sdk'
import {
  useSpaceUtil,
  type SpacePathGenerator,
} from '@tribeplatform/react-sdk/hooks'
import type { SlateRendererProps } from '@tribeplatform/react-slate-kit/components'
import { Container } from '@tribeplatform/react-ui-kit/Layout'

import { useCurrentPage } from '../hooks/useCurrentPage.js'
import { NotFound } from './Error/NotFound.js'
import { PageLayout } from './Layout/PageLayout.js'
import { PageMeta } from './PageMeta.js'
import { Space } from './Space/Space.js'
import { GenericSpaceLoading } from './Space/SpaceLoading.js'

const getSpaceSlateContext = (
  space: TSpace,
  pathname: string,
  urlParams: Readonly<Params<string>>,
  generatePath: SpacePathGenerator,
): SlateRendererProps['context'] => {
  if (space) {
    const { params } =
      legacyMatchPath(pathname, {
        path: `/${space?.address?.path ?? ''}`,
        exact: space?.address?.exact,
      }) ?? {}

    return {
      path: generatePath(space, params),
      urlParams: params || {},
    }
  }

  return {
    path: pathname,
    urlParams,
  }
}

export const SpaceRoutes: FC<{
  network: Network
}> = ({ network }) => {
  const { $t } = useI18n()
  const urlParams = useParams()
  const pathname = useRouterPath()
  const { generatePath } = useSpaceUtil()
  const {
    data: page,
    error,
    isInitialLoading,
  } = useCurrentPage({
    useQueryOptions: {
      retry: 0,
    },
  })
  const { space } = page || {}
  const context = getSpaceSlateContext(space, pathname, urlParams, generatePath)

  if (isInitialLoading && !space) {
    return (
      <PageLayout
        forceLayout={
          network?.navigationSlates?.headerDisabled
            ? SlateLayout.noHeaderNoSidebar
            : SlateLayout.headerNoSidebar
        }
      >
        <Container
          size="full"
          padding={{
            vertical: 'md',
            horizontal: { size: 'md', disableOnMobile: true },
          }}
        >
          <GenericSpaceLoading />
        </Container>
      </PageLayout>
    )
  }

  if (!space || isNotFound(error) || isForbidden(error)) {
    return (
      <PageLayout>
        <Container
          size="full"
          padding={{
            vertical: 'md',
            horizontal: { size: 'md', disableOnMobile: true },
          }}
        >
          <NotFound
            title={$t({
              defaultMessage: 'Page not found',
              id: 'Page.NotFound',
            })}
            description={$t({
              defaultMessage:
                'This page does not exist, is removed, or you don’t have permission to access it.',
              id: 'Page.NotFound.Description',
            })}
          />
        </Container>
      </PageLayout>
    )
  }

  return (
    <>
      {page && <PageMeta page={page} />}
      <Space network={network} space={space} context={context} />
    </>
  )
}
